<template>
  <div v-show="visible">
    <p aria-live="polite" class="sr-only">
      {{ resultsText }}
    </p>
    <div class="flex center between">
      <p :class="brandClasses.heading">
        {{ $t.storeSearch.enterLocation }}
      </p>
      <base-button v-if="showCloseButton" :aria-label="$t.close" @click="$emit('close')">
        <vf-icon name="close" size="md" />
      </base-button>
    </div>
    <base-form
      ref="formRef"
      :class="brandClasses.form"
      :form
      validate-on="submit"
      @submit="submit"
    >
      <vf-form-field v-slot="{ invalid }" name="postalCode" :rule="searchValidation">
        <div class="group flex">
          <vf-input
            v-model="form.postalCode"
            class="grow rounded-r-0 group-focus-within:b-grey-10"
            :disabled="loading"
            :invalid
            required
            @change="searchAsyncMessage = ''"
          >
            {{ $t.cityStateOrZipCode }}
          </vf-input>
          <base-button
            :aria-label="$t.searchLabel"
            class="w-12 flex center b b-l-0 rounded-l-0 rounded-r-xs c-grey-30 group-focus-within:b-grey-10"
            :class="[loading ? 'bg-grey-90 b-grey-50' : 'bg-white b-grey-40', {
              'b-red-30': !loading && invalid,
            }]"
            :disabled="loading"
            type="submit"
          >
            <vf-icon name="search" size="lg" />
          </base-button>
        </div>
      </vf-form-field>
      <vf-checkbox v-if="!filterBopisStores" v-model="form.onlyStoresWithAvailability" :size="availabilityCheckboxSize">
        {{ $t.storeSearch.sortByAvailability }}
      </vf-checkbox>
      <p v-if="showNoStoresMessage && !noStoreExtendedDescription" >
        {{ replaceAll($t.storeSearch[filterBopisStores ? 'noStoresFilter' : 'noStores'], { distance }) }}
      </p>
      <p v-else-if="visibleStores.length && visibleStores.length < limitStores" >
        {{ replaceAll($t.storeSearch.lessStores, { distance }) }}
      </p>
    </base-form>
    <bopis-stores
      v-if="visibleStores.length"
      :class="[{ '<lg:mb-4': showSaveButton }, brandClasses.list]"
      :model-value="showSaveButton ? preselectedStore : selectedStore"
      :show-address-preview="filterBopisStores"
      :stores="visibleStores"
      @update:model-value="changeStore"
    >
      <template #store-body-collapsed="{ store }">
        <slot name="store-body-collapsed" :store />
      </template>
    </bopis-stores>
    <template v-if="showSaveButton">
      <div v-if="visibleStores.length" :class="brandClasses.footer">
        <vf-button
          :class="brandClasses.submitButton"
          :disabled="loading || !preselectedStore"
          @click="applyPreselectedStore"
        >
          {{ $t.selectStore }}
        </vf-button>
      </div>
      <div v-else-if="showNoStoresMessage && noStoreExtendedDescription" class="mt-4 subtitle-3 <md:px-4 ">
        {{ replaceAll($t.storeSearch[filterBopisStores ? 'noStoresFilter' : 'noStores'], { distance }) }}
      </div>
      <div v-else class="mt-4" />
    </template>
  </div>
</template>

<script lang="ts" setup>
import type { BaseForm as BaseFormType } from '#components'
import type { SearchPayload } from '#root/api/clients/stores/data-contracts'
import type { LocatorSearchItem } from '#types/brandify'

const props = defineProps<{
  /**
   * Injection point for modified store objects;
   * The parent has the option to read the stores from the Pinia store,
   * modify them and pass them in this prop for visibility checks.
   * If the prop is not provided, the Pinia store values will be used directly
   */
  stores?: LocatorSearchItem[]
  /**
   * Hides the content of the form if there is no selected store until a round of geolocation has been done
   */
  initiallyHidden?: boolean
  /**
   * If true, the store search will be filtered
   * and the checkbox that allows stores to be filtered by availability will be removed
   */
  filterBopisStores?: boolean
  /**
   * Displays save button on BOPIS PLP only
   */
  showSaveButton?: boolean
  /**
   * Displays close button
   */
  showCloseButton?: boolean
}>()

const emit = defineEmits<{
  close: []
}>()

const enum ApiErrorCode {
  INVALID_CRITERIA = 8009,
  NO_STORES = 5001,
}

const { stores: storesApi } = useApi()
const {
  availabilityCheckboxSize,
  brandClasses,
  distance,
  limitStores,
  noStoreExtendedDescription
} = useAppConfig().components.form.bopisStoreSearch
const { validateMaxLength, validateRequired } = useLocalizedValidators()
const { $pluralize, $t } = useNuxtApp()
const toast = useToaster()

const { form, stores: _stores, selectedStore } = storeToRefs(useBopisStore())
const stores = computed(() => props.stores || _stores.value)
const preselectedStore = ref(selectedStore.value)
const formRef = ref<InstanceType<typeof BaseFormType>>()
const visible = ref(!props.initiallyHidden || !!selectedStore.value)
const loading = ref(false)
const noStores = ref(false)
const searched = ref(false)
const resultsText = ref('')
let searchAsyncMessage = ''

const isSTS = ({ sts_enabled }: LocatorSearchItem) => sts_enabled === '1'
const isBopis = ({ bopis_enabled, quantity }: LocatorSearchItem) => (props.filterBopisStores || quantity) && bopis_enabled === '1'

const visibleStores = computed(() => {
  if (!props.filterBopisStores && form.value.onlyStoresWithAvailability) {
    const bopisStores: LocatorSearchItem[] = []
    const stsStores: LocatorSearchItem[] = []

    for (const store of stores.value) {
      if (isBopis(store)) bopisStores.push(store)
      else if (isSTS(store)) stsStores.push(store)

      if (bopisStores.length === limitStores)
        return bopisStores
    }
    return bopisStores.concat(
      stsStores.slice(0, limitStores - bopisStores.length)
    )
  }
  else {
    return stores.value.filter((store) => isSTS(store) || isBopis(store)).slice(0, limitStores)
  }
})

const showNoStoresMessage = computed(() => noStores.value || (searched.value && !visibleStores.value.length))

const searchValidation: Record<string, Validator> = {
  searchRequired: validateRequired($t.cityStateOrZipCode),
  searchMaxLength: validateMaxLength(256),
  searchAsyncError: () => searchAsyncMessage
}

const setErrorMessage = (code: ApiErrorCode) => {
  if (code === ApiErrorCode.INVALID_CRITERIA) {
    searchAsyncMessage = $t.storeSearch.locationNotRecognized
    formRef.value?.validate(true)
  }
  else if (code === ApiErrorCode.NO_STORES) {
    noStores.value = true
  }
}

const getStores = async (payload: SearchPayload) => {
  let _stores: LocatorSearchItem[] = []
  noStores.value = false
  loading.value = true
  try {
    const { response: storeCollection, code } = await storesApi.$search(payload, {
      enhancedStoreSearch: true,
      ...(props.filterBopisStores ? { filterBOPISStores: true } : {})
    }).then((res) => ({
      response: res.response.collection.map((store) => ({
        ...store,
        display_url: store.url,
        storeid: store.enterprise_store_identifier,
        store_description: store.localpagedescription
      })),
      code: null
    })).catch((res) => {
      return { response: [], code: res.response._data.errorDetails?.[0]?.code }
    })

    _stores = storeCollection

    if (!_stores.length)
      setErrorMessage(code)
  }
  catch (error) {
    log.warn(error)
  }
  finally {
    loading.value = false
    searched.value = true
  }
  return _stores
}

const showSuccessToast = (store: LocatorSearchItem) => {
  toast.add({ props: {
    title: $t.storeSearch.toastTitle,
    message: replaceAll($t.storeSearch.toastMessage, { name: store.name })
  }, delay: 2000 })
}

const changeStore = (store?: LocatorSearchItem, background?: boolean) => {
  if (props.showSaveButton) {
    preselectedStore.value = store
    return
  }

  selectedStore.value = store
  if (!background && store)
    showSuccessToast(store)
}

let unwatchVisibleStores = () => {}
const presetStore = () => {
  unwatchVisibleStores()
  if (!selectedStore.value) {
    unwatchVisibleStores = watchImmediate(visibleStores, () => {
      if (visibleStores.value.length) {
        changeStore(visibleStores.value[0], true)
        unwatchVisibleStores()
      }
    })
  }
  else if (selectedStore.value && props.showSaveButton) {
    preselectedStore.value = selectedStore.value
  }
}

const applyPreselectedStore = () => {
  if (preselectedStore.value) {
    selectedStore.value = preselectedStore.value
    showSuccessToast(selectedStore.value)
    closeDialogs()
  }
}

const searchByGeolocation = () => {
  if (!(navigator && 'geolocation' in navigator))
    return

  loading.value = true

  navigator.geolocation.getCurrentPosition(
    async ({ coords: { latitude, longitude } }) => {
      _stores.value = await getStores({
        distance,
        unitOfMeasure: 'mile',
        lat: latitude,
        lng: longitude,
      })
      presetStore()
      loading.value = false

      if (visibleStores.value.length)
        emit('close')
      else
        visible.value = true
    },
    (error) => {
      log.warn(error)
      visible.value = true
      loading.value = false
    }
  )
}

const submit = async () => {
  preselectedStore.value = undefined

  _stores.value = await getStores({
    postalCode: form.value.postalCode,
    distance,
    unitOfMeasure: 'mile',
  })
  presetStore()
}
const resetResultsText = () => resultsText.value = $t.storeSearch.enterLocation
const setResultsText = useDebounceFn(() => {
  resultsText.value = `${visibleStores.value.length} ${$pluralize($t.results, visibleStores.value.length)}`
}, 1000)

watch(visibleStores, setResultsText)
watch(() => form.value.postalCode, resetResultsText)

onMounted(() => {
  if (form.value.postalCode && !stores.value.length)
    submit()
  else if (!selectedStore.value)
    searchByGeolocation()
})

defineExpose({
  loading,
})
</script>
